import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { authenticatedAPICall } from '../../client/api-client';
import {
  RetourOrderStatusApproved,
  RetourOrderStatusBeingChecked,
} from '../../util/util';
import '../../elements/retour-order/retour-order';
import { showNotification } from '../../LanghoutBetonApp';
import { RetourOrder } from '../../types/types';

type RetourOrderList = RetourOrder[];

@customElement('langhout-beton-retour-orders')
export class LanghoutBetonRetourOrders extends LitElement {
  static styles = css`
    :host([disableButtons]) {
      pointer-events: none;
      opacity: 0.7;
    }
  `;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array })
  private retourOrders: RetourOrderList = [];

  constructor() {
    super();
    this.listRetourOrders();
    document.addEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
    document.addEventListener(
      'refresh-retour-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
    document.removeEventListener(
      'refresh-retour-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  refreshTaskListener = () => {
    this.listRetourOrders();
  };

  render() {
    if (this.retourOrders.length === 0) {
      return html``;
    }
    return html`
      <h2>Retour orders</h2>
      ${repeat(
        this.retourOrders,
        retourOrder => retourOrder.id,
        retourOrder => {
          return html`
            <retour-order
              .retourOrder="${retourOrder}"
              @refresh-tasks=${() => this.listRetourOrders()}
              @button-clicked=${async () => {
                this.disableButtons = true;
                if (retourOrder.status === RetourOrderStatusBeingChecked) {
                  showNotification(
                    `Retour order: ${retourOrder.order_id} goedgekeurd.`,
                  );

                  await this.taskDone(retourOrder);
                } else {
                  await this.pickUpTask(retourOrder);
                }
                this.disableButtons = false;
              }}
            ></retour-order>
          `;
        },
      )}
    `;
  }

  taskDone = async (retourOrder: RetourOrder) => {
    const path = `/retour-orders/${retourOrder.id}/status`;
    const body = {
      status: RetourOrderStatusApproved,
    };
    await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(body),
    );
    this.listRetourOrders();
  };

  pickUpTask = async (retourOrder: RetourOrder) => {
    const path = `/retour-orders/${retourOrder.id}/status`;
    const body = {
      status: RetourOrderStatusBeingChecked,
    };
    await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(body),
    );
    this.listRetourOrders();
  };

  listRetourOrders = async () => {
    const path = `/retour-orders/tasks`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && Array.isArray(result.body)) {
      this.retourOrders = result.body as RetourOrderList;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-retour-orders': LanghoutBetonRetourOrders;
  }
}
